import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Palette, Pencil, X } from 'lucide-react';

export function stripHtmlPreview(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function parseTags(s) {
  if (!s) return [];
  return String(s)
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

export function joinTags(arr) {
  return arr.join(', ');
}

export const SEV_OPTS = [
  { v: 'critico', l: 'Crítico' },
  { v: 'moderado', l: 'Moderado' },
  { v: 'leve', l: 'Leve' },
];

export const TIPO_OS_FIELDS = [
  { db: 'allow_corrective', api: 'allowCorrective', label: 'Corretiva' },
  { db: 'allow_install', api: 'allowInstall', label: 'Instalação' },
  { db: 'allow_desinstall', api: 'allowDesinstall', label: 'Desinstalação' },
  { db: 'allow_preventive', api: 'allowPreventive', label: 'Preventiva' },
  { db: 'allow_stock', api: 'allowStock', label: 'Estoque' },
  { db: 'allow_recharge', api: 'allowRecharge', label: 'Recarga' },
  { db: 'allow_measurement', api: 'allowMeasurement', label: 'Aferição' },
  { db: 'allow_counter', api: 'allowCounter', label: 'Balcão' },
];

export function normOsFlag(v) {
  if (v === 0 || v === false) return 0;
  return 1;
}

export function normSev(v) {
  const s = String(v ?? 'leve').toLowerCase();
  return SEV_OPTS.some((o) => o.v === s) ? s : 'leve';
}

export function CriticidadeDropdown({ value, disabled, rowCod, openCod, setOpenCod, onPick }) {
  const wrapRef = useRef(null);
  const open = openCod === rowCod;

  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenCod(null);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open, setOpenCod]);

  const label = SEV_OPTS.find((o) => o.v === value)?.l || 'Leve';

  return (
    <div className={`defeitos-vv-sev-select${open ? ' defeitos-vv-sev-select--open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="defeitos-vv-sev-trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Criticidade"
        onClick={() => setOpenCod(open ? null : rowCod)}
      >
        <span className="defeitos-vv-sev-trigger-label">{label}</span>
        <ChevronDown size={9.9} strokeWidth={1.8} className={`defeitos-vv-sev-chevron${open ? ' defeitos-vv-sev-chevron--open' : ''}`} aria-hidden />
      </button>
      {open ? (
        <div className="defeitos-vv-sev-dropdown" role="listbox">
          {SEV_OPTS.map((o) => (
            <div
              key={o.v}
              role="option"
              aria-selected={o.v === value}
              className="defeitos-vv-sev-option"
              data-selected={o.v === value}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onPick(o.v);
                setOpenCod(null);
              }}
            >
              {o.l}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function TipoOsFlagsDropdown({ row, disabled, rowCod, openCod, setOpenCod, onToggle }) {
  const wrapRef = useRef(null);
  const open = openCod === rowCod;

  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenCod(null);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open, setOpenCod]);

  const nOn = TIPO_OS_FIELDS.reduce((acc, f) => acc + normOsFlag(row[f.db]), 0);
  const label = nOn === TIPO_OS_FIELDS.length ? 'Todos' : `${nOn}/${TIPO_OS_FIELDS.length}`;

  return (
    <div className={`defeitos-vv-tipo-select${open ? ' defeitos-vv-tipo-select--open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="defeitos-vv-tipo-trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Tipo de OS"
        onClick={() => setOpenCod(open ? null : rowCod)}
      >
        <span className="defeitos-vv-tipo-trigger-label">{label}</span>
        <ChevronDown size={9.9} strokeWidth={1.8} className={`defeitos-vv-tipo-chevron${open ? ' defeitos-vv-tipo-chevron--open' : ''}`} aria-hidden />
      </button>
      {open ? (
        <div className="defeitos-vv-tipo-dropdown" role="menu">
          {TIPO_OS_FIELDS.map((f) => (
            <label key={f.db} className="defeitos-vv-tipo-option" onMouseDown={(e) => e.preventDefault()}>
              <input
                type="checkbox"
                checked={normOsFlag(row[f.db]) === 1}
                onChange={(e) => onToggle(row, f, e.target.checked)}
              />
              <span>{f.label}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ColorSwatchCell({ hex, enabled }) {
  const valid = hex && /^#[0-9A-Fa-f]{6}$/.test(hex);
  if (!enabled) return <span className="defeitos-vv-muted">—</span>;
  if (!valid) return <span className="defeitos-vv-muted">—</span>;
  return (
    <div className="defeitos-vv-color-display defeitos-vv-color-compact">
      <span className="defeitos-vv-color-hex">{hex.toUpperCase()}</span>
      <span className="defeitos-vv-color-swatch" style={{ background: hex }} title={hex} aria-hidden />
    </div>
  );
}

export function isDefeitoColorConfigured(hex) {
  return !!(hex && /^#[0-9A-Fa-f]{6}$/.test(hex));
}

export function ColorSwatchCellDv2({ hex }) {
  if (!isDefeitoColorConfigured(hex)) {
    return <span className="defeitos-vv-muted">—</span>;
  }
  return (
    <div className="dv2-color-cell-display">
      <span
        className="defeitos-vv-color-swatch dv2-color-swatch-only"
        style={{ background: hex }}
        title={hex}
        aria-label={`Cor ${hex.toUpperCase()}`}
      />
      <span
        className="dv2-color-config-btn ci2-modern-tooltip-host"
        data-tooltip={`${hex.toUpperCase()}\nClique para configurar`}
        aria-hidden
      >
        <Palette size={14} strokeWidth={2} />
      </span>
    </div>
  );
}

export const DEFEITOS_VV_PRESET_COLORS = [
  '#7C4DFF', '#6366F1', '#0066CC', '#0EA5E9', '#06B6D4',
  '#10B981', '#22C55E', '#84CC16', '#EAB308', '#F97316', '#EF4444', '#E55451', '#EC4899',
  '#1E293B', '#2D3748', '#4A5568', '#64748B', '#94A3B8',
  '#CBD5E1', '#E2E8F0', '#F1F5F9', '#F8F9FA', '#FFFFFF',
];

const DEFEITOS_VV_COLOR_POPOVER_W = 228;

function hexForNativeColorPicker(v) {
  const s = String(v ?? '').trim();
  return /^#[0-9A-Fa-f]{6}$/.test(s) ? s.toUpperCase() : '#64748B';
}

export function DefeitosColorPalettePopover({ open, anchorRef, draftColor, onDraftChange, onClose }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const reposition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = r.left;
    const top = r.bottom + 6;
    const maxL = window.innerWidth - DEFEITOS_VV_COLOR_POPOVER_W - 10;
    if (left > maxL) left = Math.max(10, maxL);
    if (left < 10) left = 10;
    setPos({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open, anchorRef, onClose]);

  if (!open) return null;

  const nativeVal = hexForNativeColorPicker(draftColor);

  return createPortal(
    <div
      ref={panelRef}
      className="defeitos-vv-color-popover"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: DEFEITOS_VV_COLOR_POPOVER_W,
        zIndex: 100050,
      }}
      role="dialog"
      aria-label="Paleta de cores"
    >
      <p className="defeitos-vv-color-popover-title">Cores sugeridas</p>
      <div className="defeitos-vv-color-popover-presets">
        {DEFEITOS_VV_PRESET_COLORS.map((h) => (
          <button
            key={h}
            type="button"
            className="defeitos-vv-color-preset"
            style={{ background: h }}
            title={h}
            aria-label={`Cor ${h}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onDraftChange(h)}
          />
        ))}
      </div>
      <div className="defeitos-vv-color-popover-native">
        <span className="defeitos-vv-color-popover-native-label">Qualquer cor</span>
        <input
          type="color"
          className="defeitos-vv-color-popover-picker"
          value={nativeVal}
          aria-label="Seletor de cor"
          onChange={(e) => onDraftChange(e.target.value.toUpperCase())}
        />
      </div>
    </div>,
    document.body
  );
}
